<template>
  <div :data-id="panelId" class="flex flex-col gap-5">
    <!-- Show Task Details when viewing task details -->
    <TaskDetails
      v-if="openTaskDetails && target.type.includes('Sbt')"
      :tasks="target.target_tasks"
      :panelId="panelId"
      @back="$emit('showTaskDetails', false)"
    />

    <!-- Show Problem Behavior Details when viewing behavior details -->
    <ProblemBehaviorDetails
      v-else-if="openBehaviorDetails && target.type.includes('Sbt')"
      :behaviors="target.target_problem_behaviors"
      :panelId="panelId"
      @back="$emit('showBehaviorDetails', false)"
    />

    <!-- Main content -->
    <div v-else>
      <!-- target details -->
      <div :data-id="panelId" class="px-6 py-5 bg-pure-white">
        <div
          :data-id="panelId"
          class="flex items-center justify-between gap-6 mb-1"
        >
          <BlTextBody2
            :data-id="panelId"
            class="truncate"
            :text="target.curriculum_name"
          />
          <div class="flex items-center gap-3">
            <BlInputButton
              v-if="target.type.includes('Sbt')"
              :data-id="panelId"
              label="Edit details"
              type="outlined"
              dense
              @clicked="$emit('editDetails')"
            />
            <BlIcon icon="close" color="gray" :clickable="true" />
          </div>
        </div>
        <div :data-id="panelId" class="mb-4">
          <BlTextSubtitle1 :data-id="panelId" :text="target.name" />
        </div>
        <div :data-id="panelId" class="mb-4">
          <BlTextBody2
            :data-id="panelId"
            :text="target.description"
            class="whitespace-pre-line"
          />
        </div>
        <div :data-id="panelId" class="flex gap-2 mb-2">
          <BlIcon
            :data-id="panelId"
            icon="document-normal"
            color="slate-6"
            class="w-4 h-4"
          />
          <BlTextBody2 :data-id="panelId" :text="targetTypes[target.type]" />
          <BlTextBody2
            v-if="target.type.includes('Prompting')"
            :data-id="panelId"
            :text="`(${target.prompting_format})`"
            class="capitalize"
          />
        </div>
        <div
          v-if="!target.type.includes('Sbt')"
          :data-id="panelId"
          class="flex items-start gap-2 mb-2"
        >
          <BlIcon
            :data-id="panelId"
            icon="goal"
            color="slate-6"
            class="w-4 mt-0.5"
          />
          <BlTextBody2
            :data-id="panelId"
            :text="generateSuccessMetric(target)"
          />
        </div>
        <div
          :data-id="panelId"
          v-if="target.type.includes('Percentage')"
          class="flex gap-2"
        >
          <BlIcon
            :data-id="panelId"
            icon="tick-square"
            color="slate-6"
            class="w-4 h-4"
          />
          <BlTextBody2
            :data-id="panelId"
            :text="`${target.number_of_trial} trial(s)`"
          />
        </div>
        <div
          :data-id="panelId"
          v-if="target.type.includes('Pir')"
          class="flex gap-2"
        >
          <BlIcon :data-id="panelId" icon="timer" color="slate-6" class="w-5" />
          <BlTextBody2
            :data-id="panelId"
            :text="`${target.duration} minute(s)`"
          />
        </div>
      </div>
      <!-- end target details -->

      <!-- probing -->
      <div v-if="target.probing_enable" :data-id="panelId">
        <div :data-id="panelId" class="w-full h-5 bg-slate-200"></div>
        <div
          :data-id="panelId"
          v-if="target.probing_enable"
          class="px-6 py-5 bg-pure-white"
        >
          <div :data-id="panelId" class="mb-1">
            <BlTextSubtitle2 :data-id="panelId" text="Probing" />
          </div>
          <div :data-id="panelId" class="mb-4">
            <BlTextBody2
              :data-id="panelId"
              text="Probing can be employed to assess a skill at various stages of your therapy."
            />
          </div>
          <div :data-id="panelId" class="flex items-start gap-2 mb-2">
            <BlIcon
              :data-id="panelId"
              icon="goal"
              color="slate-6"
              class="w-4 mt-0.5"
            />
            <BlTextBody2
              :data-id="panelId"
              :text="`≥ ${target.probing_goal}%`"
            />
          </div>
          <div :data-id="panelId" class="flex gap-2">
            <BlIcon
              :data-id="panelId"
              icon="tick-square"
              color="slate-6"
              class="w-4 h-4"
            />
            <BlTextBody2
              :data-id="panelId"
              :text="`${target.probing_number_of_trial} trial(s)`"
            />
          </div>
        </div>
      </div>
      <!-- end probing -->

      <!-- prompting -->
      <div v-if="target.type.includes('Prompting')" :data-id="panelId">
        <div :data-id="panelId" class="w-full h-5 bg-slate-200"></div>
        <div :data-id="panelId" class="px-6 py-5 bg-pure-white">
          <div :data-id="panelId" class="mb-1">
            <BlTextSubtitle2 :data-id="panelId" text="Prompting" />
          </div>
          <div :data-id="panelId" class="mb-4">
            <BlTextBody2
              :data-id="panelId"
              text="Here are the prompts used in this target:"
            />
          </div>
          <div :data-id="panelId" class="flex items-center gap-2 mb-2">
            <BlTextSubtitle3
              v-if="target.prompting_format === 'custom'"
              :data-id="panelId"
              text="Score (%)"
              class="flex-shrink-0 w-20 text-slate-700"
            />
            <BlTextSubtitle3
              :data-id="panelId"
              text="Code"
              class="flex-shrink-0 w-10 text-slate-700"
            />
            <BlTextSubtitle3
              :data-id="panelId"
              text="Symbol"
              class="flex-shrink-0 w-15 text-slate-700"
            />
            <BlTextSubtitle3
              :data-id="panelId"
              text="Prompt name"
              class="w-full text-slate-700"
            />
          </div>
          <div
            v-if="target.prompting_format === 'classic'"
            :data-id="panelId"
            class="flex flex-col gap-2"
          >
            <PromptingClientItem
              v-for="prompt in target.prompts"
              :key="prompt.id"
              :data-id="panelId"
              :prompt="prompt"
              :format="target.prompting_format"
              readOnly
            />
          </div>
          <div
            v-if="target.prompting_format === 'custom'"
            :data-id="panelId"
            class="flex flex-col gap-2"
          >
            <div
              v-for="group in groupedCustomPromptingPrompts"
              :key="group.score"
              :data-id="panelId"
              class="flex gap-2"
            >
              <div
                :data-id="panelId"
                class="flex items-center justify-center flex-shrink-0 w-20 text-center border rounded bg-prim-100 border-prim-100"
              >
                <BlTextBody1
                  :data-id="panelId"
                  :text="group.score"
                  class="font-semibold text-light-purple"
                />
              </div>
              <div :data-id="panelId" class="flex flex-col gap-2 truncate">
                <PromptingClientItem
                  v-for="prompt in group.prompts"
                  :key="prompt.id"
                  :data-id="panelId"
                  :prompt="prompt"
                  :format="target.prompting_format"
                  readOnly
                />
              </div>
            </div>
          </div>

          <BlInputButton
            :data-id="panelId"
            class="w-full mt-8"
            type="outlined"
            dense
            :label="'Edit prompts'"
            @clicked="$emit('editPrompting')"
          />
        </div>
      </div>
      <!-- end prompting -->

      <!-- sbt -->
      <div :data-id="panelId" v-if="target.type.includes('Sbt')">
        <div :data-id="panelId" class="w-full h-5 bg-slate-200"></div>
        <div :data-id="panelId" class="px-6 py-5 bg-pure-white">
          <div :data-id="panelId" class="mb-1">
            <BlTextSubtitle2 :data-id="panelId" text="Prompts" />
          </div>
          <div :data-id="panelId" class="mb-4">
            <BlTextBody2
              :data-id="panelId"
              text="Here are the prompts used in this target:"
            />
          </div>
          <div :data-id="panelId" class="flex items-center gap-2 mt-3 mb-10">
            <div class="flex-shrink-0 w-20">
              <BlTextBody2 text="Score (%)" class="text-slate-700" />
            </div>
            <div class="w-full ">
              <BlTextBody2 text="Prompt name" class="text-slate-700" />
            </div>
          </div>
          <div :data-id="panelId" class="flex flex-col gap-4 space-y-5">
            <div
              v-for="prompt in groupedPrompts"
              :key="prompt.id"
              :data-id="panelId"
              class="flex items-center h-6 gap-2"
            >
              <div
                class="flex items-center justify-center w-20 rounded h-14 bg-prim-100"
              >
                <BlTextBody2
                  :text="prompt.score"
                  class="text-lg font-semibold text-light-purple"
                />
              </div>
              <BlTextBody2 :text="prompt.name" class="text-slate-1000" />
            </div>
          </div>

          <BlInputButton
            :data-id="panelId"
            class="w-full mt-8"
            type="outlined"
            dense
            :label="'Edit prompts'"
            @clicked="$emit('editPromptSbt')"
          />
        </div>
        <div :data-id="panelId" class="w-full h-5 bg-slate-200"></div>
        <div :data-id="panelId" class="px-6 py-5 bg-pure-white">
          <div :data-id="panelId" class="flex items-center gap-3 mb-1">
            <BlTextSubtitle2 :data-id="panelId" text="Tasks" />
            <BlBubbleCount
              :data-id="panelId"
              :count="target.target_tasks.length"
            />
          </div>
          <div :data-id="panelId" class="flex flex-col gap-4 mt-4 space-y-2">
            <div
              v-for="task in target.target_tasks"
              :key="task.id"
              :data-id="panelId"
              class="flex items-center h-6 gap-2"
            >
              <div class="flex w-full gap-3 p-2 border-b border-slate-400 ">
                <BlTextBody2
                  :text="`${task.code} - ${task.title}`"
                  class="truncate text-slate-1000"
                />
              </div>
            </div>
          </div>

          <div class="flex items-center gap-3 mt-8">
            <BlInputButton
              :data-id="panelId"
              class="w-38"
              type="outlined"
              :label="'Edit tasks'"
              dense
              @clicked="$emit('editTask')"
            />
            <BlInputButton
              :data-id="panelId"
              class="w-38"
              type="outlined"
              dense
              @clicked="$emit('showTaskDetails', true)"
              :label="'View details'"
            />
          </div>
        </div>
        <div :data-id="panelId" class="w-full h-5 bg-slate-200"></div>
        <div :data-id="panelId" class="px-6 py-5 bg-pure-white">
          <div :data-id="panelId" class="flex items-center gap-3 mb-1">
            <BlTextSubtitle2 :data-id="panelId" text="Problem behavior" />
            <BlBubbleCount
              :data-id="panelId"
              :count="target.target_problem_behaviors.length"
            />
          </div>
          <div :data-id="panelId" class="flex flex-col gap-4 mt-4 space-y-2">
            <div
              v-for="pb in target.target_problem_behaviors"
              :key="pb.id"
              :data-id="panelId"
              class="flex items-center h-6 gap-2"
            >
              <div class="flex w-full gap-3 p-2 border-b border-slate-400 ">
                <BlTextBody2
                  :text="`${pb.code} - ${pb.code_definition}`"
                  class="truncate text-slate-1000"
                />
              </div>
            </div>
          </div>

          <div class="flex items-center gap-3 mt-8 mb-10">
            <BlInputButton
              :data-id="panelId"
              class="w-38"
              type="outlined"
              dense
              :label="'Edit behaviors'"
              @clicked="$emit('editBehavior')"
            />
            <BlInputButton
              :data-id="panelId"
              class="w-38"
              type="outlined"
              dense
              @clicked="$emit('showBehaviorDetails', true)"
              :label="'View details'"
            />
          </div>
        </div>
      </div>
      <!-- end sbt -->

      <!-- action recommendation -->
      <div
        :data-id="panelId"
        v-if="actionRecommendation && !target.type.includes('Sbt')"
      >
        <div :data-id="panelId" class="w-full h-5 bg-slate-200"></div>
        <div :data-id="panelId" class="px-6 py-5 bg-pure-white">
          <div :data-id="panelId" class="flex items-center gap-2 mb-4">
            <div
              :data-id="panelId"
              class="p-1 bg-orange-300"
              :style="{ borderRadius: '4px' }"
            >
              <BlIcon :data-id="panelId" icon="tooltip" class="w-5 h-5" />
            </div>
            <BlTextSubtitle2 :data-id="panelId" text="Action recommendations" />
          </div>
          <div
            :data-id="panelId"
            class="p-4 border rounded bg-slate-100 border-slate-400"
          >
            <div :data-id="panelId" class="mb-4 space-y-1">
              <BlTextSubtitle3
                :data-id="panelId"
                text="Passing success metric"
              />
              <div :data-id="panelId" class="flex flex-wrap items-center gap-2">
                <BlTextBody2
                  :data-id="panelId"
                  text="Updating the current target's status from"
                />
                <StatusChip :data-id="panelId" status="in_progress" dense />
                <BlTextBody2 :data-id="panelId" text="to" />
                <StatusChip :data-id="panelId" status="mastered" dense />
              </div>
            </div>
            <div
              :data-id="panelId"
              v-if="
                actionRecommendation.total_success === null &&
                  actionRecommendation.consecutive_success === null
              "
            >
              <BlTextBody2
                :data-id="panelId"
                class="italic"
                text="Currently, no action has been set for this recommendation."
              />
            </div>
            <div :data-id="panelId" v-else class="pl-6 space-y-4">
              <div
                :data-id="panelId"
                v-if="actionRecommendation.total_success !== null"
                class="space-y-1"
              >
                <div :data-id="panelId" class="flex items-center gap-2">
                  <BlTextSubtitle3 :data-id="panelId" text="Total success" />
                </div>
                <div
                  :data-id="panelId"
                  class="flex flex-wrap items-center gap-1"
                >
                  <BlTextBody2
                    :data-id="panelId"
                    text="Receive a recommendation after"
                  />
                  <BlTextBody2 :data-id="panelId" text="completing" />
                  <BlTextBody2
                    :data-id="panelId"
                    class="font-bold"
                    :text="`at least ${actionRecommendation.total_success}`"
                  />
                  <BlTextBody2 :data-id="panelId" text="successful" />
                  <BlTextBody2 :data-id="panelId" text="session(s)." />
                </div>
              </div>
              <div
                :data-id="panelId"
                v-if="actionRecommendation.consecutive_success !== null"
                class="space-y-1"
              >
                <div :data-id="panelId" class="flex items-center gap-2">
                  <BlTextSubtitle3
                    :data-id="panelId"
                    text="Consecutive success"
                  />
                </div>
                <div
                  :data-id="panelId"
                  class="flex flex-wrap items-center gap-1"
                >
                  <BlTextBody2
                    :data-id="panelId"
                    text="Receive a recommendation only if the"
                  />
                  <BlTextBody2
                    :data-id="panelId"
                    text="session is successful for"
                  />
                  <BlTextBody2
                    :data-id="panelId"
                    class="font-bold"
                    :text="
                      `${actionRecommendation.consecutive_success} consecutive`
                    "
                  />
                  <BlTextBody2
                    :data-id="panelId"
                    class="font-bold"
                    :text="`session(s).`"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- end action recommendation -->
    </div>
  </div>
</template>

<script>
import StatusChip from "../../clients/target/StatusChip.vue";
import PromptingClientItem from "../prompting/ClientItem.vue";
import TaskDetails from "./TaskDetails.vue";
import ProblemBehaviorDetails from "./ProblemBehaviorDetails.vue";

import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlBubbleCount from "../../BlBubbleCount.vue";

export default {
  name: "RepositoryDatabankDetails",
  components: {
    StatusChip,
    PromptingClientItem,
    TaskDetails,
    ProblemBehaviorDetails,
    //
    BlIcon,
    BlInputButton,
    BlTextBody1,
    BlTextBody2,
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlBubbleCount
  },
  props: {
    target: { type: Object },
    panelId: { type: String },
    openTaskDetails: { type: Boolean, default: false },
    openBehaviorDetails: { type: Boolean, default: false }
    // @editPrompting
    // @editTask
    // @editBehavior
    // @editPromptSbt
    // @showTaskDetails
    // @showBehaviorDetails
    // @editDetails
  },
  data() {
    return {
      targetTypes: {
        "Target::Percentage": "Percentage",
        "Target::Pir": "Partial interval recording",
        "Target::Duration": "Duration",
        "Target::Frequency": "Frequency",
        "Target::Prompting": "Prompting",
        "Target::Sbt": "Skill-Based Treatment (SBT)"
      },
      editDetailsOpened: false
    };
  },
  computed: {
    actionRecommendation() {
      if (!this.target) return null;
      const empty = {
        id: null,
        target_id: this.target.id,
        total_success: null,
        consecutive_success: null
      };
      return this.target.action_recommendations &&
        this.target.action_recommendations.at(0)
        ? this.target.action_recommendations.at(0)
        : empty;
    },
    groupedCustomPromptingPrompts() {
      const prompts = this.target.prompts;
      if (!prompts.length) return [];

      const sortedPrompts = prompts.sort((a, b) => {
        if (b.score === a.score) {
          return a.is_default ? 1 : -1;
        }
        return b.score - a.score;
      });

      const grouped = sortedPrompts.reduce((acc, prompt) => {
        const score = prompt.score;
        if (!acc[score]) {
          acc[score] = [];
        }
        acc[score].push(prompt);
        return acc;
      }, {});

      const groupedArray = Object.keys(grouped).map(score => {
        return {
          score: parseInt(score),
          prompts: grouped[score].sort((a, b) => a.position - b.position)
        };
      });

      return groupedArray.sort((a, b) => b.score - a.score);
    },
    groupedPrompts() {
      const grouped = this.target.prompts.reduce((acc, prompt) => {
        if (!acc[prompt.score]) {
          acc[prompt.score] = { score: prompt.score, names: [] };
        }
        acc[prompt.score].names.push(prompt.name);
        return acc;
      }, {});

      const groupedArray = Object.values(grouped);

      groupedArray.sort((a, b) => b.score - a.score);

      return groupedArray.map(group => ({
        score: group.score,
        name: group.names.join(", ")
      }));
    }
  },
  methods: {
    generateSuccessMetric(target) {
      let prefix = "";
      let goalText = "";
      let suffix = "";

      // prefix
      if (target.success_metric === "equal to or greater than goal") {
        prefix = "≥ ";
      }
      if (target.success_metric === "less than goal") {
        prefix = "< ";
      }

      // goal text
      if (target.type.includes("Duration")) {
        goalText = target.goal_time;
      } else if (
        target.type.includes("Prompting") &&
        target.prompting_format === "classic"
      ) {
        goalText = `${target.success_metric} prompt min. ${target.goal}`;
      } else {
        goalText = target.goal;
      }

      // suffix
      if (target.type.includes("Percentage")) suffix = "%";
      if (target.type.includes("Pir")) suffix = "%";
      if (
        target.type.includes("Prompting") &&
        target.prompting_format === "custom"
      ) {
        suffix = "%";
      }
      if (target.type.includes("Frequency")) suffix = " attempt(s) per session";
      if (
        target.type.includes("Prompting") &&
        target.prompting_format === "classic"
      ) {
        suffix = " attempt(s) per session";
      }

      return prefix + goalText + suffix;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/repository/databank/PanelDetails.vue